
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { UseFormReturn } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

// Type should be in sync with the one in types.ts
export interface MilestoneReward {
  id: string;
  threshold: number;
  reward: string;
  rewardType?: 'one-time' | 'recurring' | 'cumulative';
  isPercentage?: boolean;
}

interface MilestoneThresholdsSectionProps {
  form: UseFormReturn<any>;
  campaignType?: string; // Add campaign type to customize for newsletter
}

export const MilestoneThresholdsSection: React.FC<MilestoneThresholdsSectionProps> = ({ 
  form, 
  campaignType 
}) => {
  const value = form.watch("milestoneRewards") || [];
  const isNewsletterReferral = campaignType === 'newsletter-referral';
  
  const setValue = (v: MilestoneReward[]) =>
    form.setValue("milestoneRewards", v, { shouldDirty: true, shouldValidate: true });

  // Enhanced to add default rewardType for new milestones
  const handleAdd = () => {
    setValue([
      ...value,
      { 
        id: uuidv4(), 
        threshold: 1, 
        reward: "", 
        rewardType: isNewsletterReferral ? 'recurring' : 'one-time',
        isPercentage: false
      }
    ]);
  };

  const handleRemove = (id: string) => {
    setValue(value.filter((m: MilestoneReward) => m.id !== id));
  };

  const handleChange = (id: string, field: keyof MilestoneReward, fieldValue: any) => {
    setValue(
      value.map((m: MilestoneReward) =>
        m.id === id ? { ...m, [field]: field === 'threshold' ? parseInt(fieldValue) || 1 : fieldValue } : m
      )
    );
  };

  // Sort milestones by threshold value (ascending order)
  React.useEffect(() => {
    if (value.length > 1) {
      const sortedMilestones = [...value].sort((a, b) => a.threshold - b.threshold);
      if (JSON.stringify(sortedMilestones) !== JSON.stringify(value)) {
        setValue(sortedMilestones);
      }
    }
  }, [value]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {isNewsletterReferral ? "Newsletter Referral Rewards" : "Milestone Thresholds and Rewards"}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          {isNewsletterReferral 
            ? "Define how many referrals subscribers need for each reward and what they'll receive."
            : "Define the referral milestones and what the user receives for reaching each."}
        </p>
        {value.length === 0 && (
          <div className="text-center text-gray-500 py-2">
            No {isNewsletterReferral ? "reward rules" : "milestones"} added yet.
          </div>
        )}
        {value.map((m: MilestoneReward, i: number) => (
          <div key={m.id} className="flex flex-col gap-2 border p-4 rounded-md">
            <div className="flex gap-2 items-center">
              <div className="flex items-center gap-2 flex-1">
                <label className="text-xs font-medium min-w-24">
                  {isNewsletterReferral ? "Referrals needed" : "Threshold"}
                </label>
                <Input
                  type="number"
                  min={1}
                  value={m.threshold}
                  onChange={e => handleChange(m.id, "threshold", e.target.value)}
                  className="w-20"
                />
                <label className="text-xs font-medium ml-4 min-w-16">Reward</label>
                <div className="flex flex-1 gap-2">
                  <Input
                    value={m.reward}
                    onChange={e => handleChange(m.id, "reward", e.target.value)}
                    className="flex-1"
                    placeholder={isNewsletterReferral ? "e.g. Free month" : "e.g. $5 Gift Card"}
                  />
                  {isNewsletterReferral && (
                    <Switch
                      checked={m.isPercentage}
                      onCheckedChange={(checked) => handleChange(m.id, "isPercentage", checked)}
                      id={`percentage-${m.id}`}
                    />
                  )}
                  {isNewsletterReferral && (
                    <Label htmlFor={`percentage-${m.id}`} className="text-xs">
                      {m.isPercentage ? "%" : "$"}
                    </Label>
                  )}
                </div>
              </div>
              <Button size="icon" variant="destructive" onClick={() => handleRemove(m.id)} type="button">
                ×
              </Button>
            </div>
            
            {isNewsletterReferral && (
              <div className="flex items-center gap-2 mt-2">
                <label className="text-xs font-medium min-w-24">Reward Type</label>
                <Select
                  value={m.rewardType || 'one-time'}
                  onValueChange={(value) => handleChange(m.id, "rewardType", value)}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one-time">One-time reward</SelectItem>
                    <SelectItem value="recurring">Recurring (every X referrals)</SelectItem>
                    <SelectItem value="cumulative">Cumulative (X total referrals)</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex-1 text-xs text-gray-500">
                  {m.rewardType === 'one-time' && "User gets this reward only once when they reach this number of referrals"}
                  {m.rewardType === 'recurring' && `User gets this reward every time they refer ${m.threshold} new people`}
                  {m.rewardType === 'cumulative' && "User gets this reward when they reach this total number of referrals"}
                </div>
              </div>
            )}
          </div>
        ))}
        <Button type="button" variant="outline" onClick={handleAdd}>
          Add {isNewsletterReferral ? "Reward Rule" : "Milestone"}
        </Button>
      </CardContent>
    </Card>
  );
};
